/**
 * @file CardsSection.tsx
 * @brief Billing → Cards: list, add (Stripe), set default, remove.
 *
 * @details
 * Removing the default card promotes the next one, and removing the last
 * card warns first (cardRemoval.ts). Actions on one card sit in its ⋯ menu.
 *
 * @author DES
 * @date 17-10-2026
 */

import { CreditCard, Ellipsis, Star, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/feedback/EmptyState";
import { GridBand, GridCell } from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { isStripeConfigured } from "@/shared/stripe/stripeClient";
import {
  CardConfirmStatus,
  ConfirmCardBanner,
  ConfirmWithBankLabel,
  useConfirmSavedCard,
} from "@/shared/billing/ConfirmSavedCard";
import {
  createMyConfirmIntent,
  myBillingKeys,
  useMyCards,
  useMySubscription,
  useRemoveMyCard,
  useSetMyDefaultCard,
} from "../api";
import { brandName, planCardRemoval } from "../cardRemoval";
import { AddMyCardSheet } from "./AddMyCardSheet";
import { CardTiles } from "@/shared/billing/CardTiles";
import { formatDay } from "./format";

export function CardsSection() {
  const cardsQuery = useMyCards();
  const status = useMySubscription();
  const setDefault = useSetMyDefaultCard();
  const remove = useRemoveMyCard();
  const [addOpen, setAddOpen] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const stripeReady = isStripeConfigured();
  // "Confirm with your bank" on a saved card (21-10-2026): the phone sends
  // people here when their bank asks for a check.
  const qc = useQueryClient();
  const refreshCards = useCallback(() => void qc.invalidateQueries({ queryKey: myBillingKeys.cards() }), [qc]);
  const bank = useConfirmSavedCard({ createIntent: createMyConfirmIntent, onConfirmed: refreshCards });

  const defaultId = cardsQuery.data?.defaultPaymentMethod ?? null;
  const cards = useMemo(() => {
    const list = cardsQuery.data?.cards ?? [];
    return [...list].sort((a, b) => Number(b.id === defaultId) - Number(a.id === defaultId));
  }, [cardsQuery.data, defaultId]);

  const sub = status.data?.subscription;
  const renews = sub && !sub.cancelAtPeriodEnd ? formatDay(sub.currentPeriodEnd) : null;
  const removal = removeId
    ? planCardRemoval(cardsQuery.data?.cards ?? [], defaultId, removeId, { planRenews: renews })
    : null;
  const removing = cards.find((c) => c.id === removeId);

  const fail = (title: string) => (err: unknown) =>
    toast({
      variant: "destructive",
      title,
      description: err instanceof ApiClientError ? err.message : "Please try again.",
    });

  return (
    <>
      <GridBand className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold tracking-tight">Cards</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Your default card pays for your plan and pay-as-you-go use.
          </p>
        </div>
      </GridBand>

      <GridCell padded={false}>
        {stripeReady && !cardsQuery.isLoading ? (
          <ConfirmCardBanner
            cards={cards}
            defaultId={defaultId}
            brandLabel={brandName}
            states={bank.states}
            onConfirm={(id) => void bank.confirm(id)}
            onAddCard={() => setAddOpen(true)}
          />
        ) : null}
        {!stripeReady ? (
          <div className="border-b border-border/60 px-4 py-3 text-sm text-warning md:px-5">
            Card payments aren't set up on this site yet.
          </div>
        ) : null}
        {cardsQuery.isLoading ? (
          <SkeletonRows rows={2} twoLine />
        ) : cardsQuery.isError ? (
          <div className="px-4 py-3 text-sm text-destructive md:px-5">
            Couldn't load your cards.{" "}
            <button type="button" className="underline" onClick={() => void cardsQuery.refetch()}>
              Try again
            </button>
          </div>
        ) : cards.length === 0 ? (
          <EmptyState
            shape="invoice"
            title="No cards yet"
            description="Add a card to buy a plan, record meetings and start interpretation sessions."
            action={
              stripeReady ? (
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="inline-flex h-12 items-center gap-2 rounded-full bg-brand px-6 text-[15px] font-semibold text-white transition-colors hover:bg-brand/90"
                >
                  <CreditCard className="h-5 w-5" />
                  Add a card
                </button>
              ) : null
            }
          />
        ) : (
          <CardTiles
            cards={cards.map((c) => ({ ...c, isDefault: c.id === defaultId }))}
            below={(card) => <CardConfirmStatus state={bank.states[card.id]} />}
            onAdd={stripeReady ? () => setAddOpen(true) : undefined}
            addDisabled={cardsQuery.isLoading}
            menu={(card) => {
              const isDefault = card.isDefault;
              return (
                  <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for card ending ${card.last4}`}>
                                <Ellipsis className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {stripeReady ? (
                                <DropdownMenuItem
                                  disabled={bank.states[card.id]?.status === "running"}
                                  onSelect={() => void bank.confirm(card.id)}
                                >
                                  <ConfirmWithBankLabel />
                                </DropdownMenuItem>
                              ) : null}
                              {!isDefault ? (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    setDefault
                                      .mutateAsync(card.id)
                                      .then(() => toast({ variant: "success", title: "Default card updated" }))
                                      .catch(fail("Couldn't change the default card"))
                                  }
                                >
                                  <Star className="h-4 w-4" /> Make default
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => setRemoveId(card.id)}
                              >
                                <Trash2 className="h-4 w-4" /> Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
              );
            }}
          />
        )}
      </GridCell>

      <AddMyCardSheet open={addOpen} onOpenChange={setAddOpen} makeDefault={cards.length === 0} />

      <AlertDialog open={Boolean(removeId)} onOpenChange={(o) => !o && setRemoveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {removing ? `${brandName(removing.brand)} •••• ${removing.last4}` : "card"}?
            </AlertDialogTitle>
            <AlertDialogDescription>{removal?.warning ?? "The card will be removed from your account."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep card</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!removeId) return;
                const id = removeId;
                setRemoveId(null);
                remove
                  .mutateAsync({ id, promoteId: removal?.promote?.id })
                  .then(() => toast({ variant: "success", title: "Card removed" }))
                  .catch(fail("Couldn't remove the card"));
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
